import * as React from 'react';

import { cn } from '@sunfish/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import {
	HOUR_OPTIONS,
	joinTimeValue,
	minuteOptions,
	splitTimeValue,
	type Meridiem,
	type TimeParts
} from './time-select-value';

/**
 * Hour / minute / AM-PM dropdowns over a 24-hour "HH:mm" value.
 *
 * The native <input type="time"> this replaces rendered differently in every
 * browser and gave no hint that a keyboard was expected. Three lists are slower
 * to build but say exactly what they want.
 */
export function TimeSelect({
	id,
	value,
	onChange,
	disabled,
	className
}: {
	id?: string;
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	className?: string;
}) {
	const parsed = splitTimeValue(value);
	// Held only while the three lists are partly filled; once they make a whole
	// time it leaves through onChange and `parsed` takes over again.
	const [draft, setDraft] = React.useState<Partial<TimeParts>>({});
	const parts: Partial<TimeParts> = parsed ?? draft;

	const update = (patch: Partial<TimeParts>) => {
		const next = { ...parts, ...patch };
		if (next.hour12 && next.minute && next.meridiem) {
			setDraft({});
			onChange(joinTimeValue(next as TimeParts));
			return;
		}
		setDraft(next);
	};

	return (
		<div className={cn('flex items-center gap-1.5', className)}>
			<Select value={parts.hour12 ?? ''} onValueChange={(hour12) => update({ hour12 })} disabled={disabled}>
				<SelectTrigger id={id} aria-label="Hour" className="w-[4.25rem]">
					<SelectValue placeholder="--" />
				</SelectTrigger>
				<SelectContent>
					{HOUR_OPTIONS.map((hour) => (
						<SelectItem key={hour} value={hour}>
							{hour}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			<span aria-hidden="true" className="text-muted-foreground">
				:
			</span>

			<Select value={parts.minute ?? ''} onValueChange={(minute) => update({ minute })} disabled={disabled}>
				<SelectTrigger aria-label="Minute" className="w-[4.25rem]">
					<SelectValue placeholder="--" />
				</SelectTrigger>
				<SelectContent>
					{minuteOptions(parts.minute).map((minute) => (
						<SelectItem key={minute} value={minute}>
							{minute}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			<Select
				value={parts.meridiem ?? ''}
				onValueChange={(meridiem) => update({ meridiem: meridiem as Meridiem })}
				disabled={disabled}
			>
				<SelectTrigger aria-label="AM or PM" className="w-[4.75rem]">
					<SelectValue placeholder="--" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="AM">AM</SelectItem>
					<SelectItem value="PM">PM</SelectItem>
				</SelectContent>
			</Select>
		</div>
	);
}
